import { useEffect, useRef, useState } from 'react';
import TopBanner from '@/components/back-office/TopBanner';
import { useRestaurantsContext } from '@/contexts/RestaurantContext';
import BOLayout from '@/pages/back-office/BOLayout';
import { toast } from 'react-toastify';
import { useNavigate } from 'react-router-dom';

const ProductCreation = () => {
	const { selectedRestaurant } = useRestaurantsContext();
	const role = JSON.parse(localStorage.getItem('role'));
	const [canSubmit, setCanSubmit] = useState(false);
	const [picture, setPicture] = useState({});
	const [name, setName] = useState('');
	const [price, setPrice] = useState(0);
	const [quantity, setQuantity] = useState(0);
	const [selectedCategory, setSelectedCategory] = useState('');
	const [categories, setCategories] = useState([]);
	const [weight, setWeight] = useState('');
	const [vat, setVat] = useState('');
	const [vats, setVats] = useState([]);
	const [preview, setPreview] = useState(null);
	const [maxQuantity, setMaxQuantity] = useState('');
	const pictureRef = useRef(null);
	const navigate = useNavigate();

	useEffect(() => {
		if ((role && !['super-admin', 'admin'].includes(role))) {
			toast.info('Zone interdite', {
				position: 'top-center',
				autoClose: 1000,
				hideProgressBar: false,
				closeOnClick: true,
				pauseOnHover: false,
				draggable: true,
				theme: 'colored',
			});

			navigate('/back-office');

			return;
		}
	}, [role]);

	useEffect(() => {
		if (!selectedRestaurant) {
			return;
		}

		const fetchData = async () => {
			try {
				if (categories.length > 0) {
					return;
				}

				const response = await axios.get(`${import.meta.env.VITE_API_URL}/api/settings/categories/list`);

				if (response.status === 200) {
					setCategories(response.data);
				}

				if (vats.length > 0) {	
					return;
				}

				const vatsResponse = await axios.get(`${import.meta.env.VITE_API_URL}/api/back-office/settings/vats/list`);

				if (vatsResponse.status === 200) {
					setVats(vatsResponse.data);
				}
			} catch (error) {
				console.error(error);
			}
		};
		fetchData();
	}, [selectedRestaurant]);

	useEffect(() => {
		if (picture && name && price && selectedCategory && weight && vat) {
			setCanSubmit(true);
			return;
		}

		setCanSubmit(false);
	}, [picture, name, price, selectedCategory, weight, vat]);

	const handlePicture = (e) => {
		setPicture(e.target.files[0]);
		setPreview(URL.createObjectURL(e.target.files[0]));
	};

	const handleSubmit = async (e) => {
		e.preventDefault();

		const formData = new FormData();
		formData.append('picture', picture);
		formData.append('name', name);
		formData.append('price', price);
		formData.append('quantity', quantity);
		formData.append('category', selectedCategory);
		formData.append('weight', weight);
		formData.append('vat', vat);
		formData.append('max_quantity', maxQuantity || null);

		try {
			const response = await axios.post(
				`${import.meta.env.VITE_API_URL}/api/back-office/restaurants/${selectedRestaurant?.id}/products/create`,
				formData
			);

			if (response.status === 201) {
				toast.success('Produit crée avec succès', {
					position: 'top-center',
					autoClose: 800,
					hideProgressBar: false,
					closeOnClick: true,
					pauseOnHover: false,
					draggable: true,
					theme: 'colored',
				});
			}
		} catch (error) {
			console.error('Error creating product:', error);

			toast.error('Une erreur est survenue', {
				position: 'top-center',
				autoClose: 3500,
				hideProgressBar: false,
				closeOnClick: true,
				pauseOnHover: false,
				draggable: true,
				theme: 'colored',
			});
		}

		navigate(`/back-office/restaurants/${selectedRestaurant?.id}/products`);
	};

	return (
		<BOLayout>
			<div className="mb-12">
				<TopBanner
					role={role}
					showButtons={'left'}
					showToggle={false}
					hasTopRightButton={false}
				/>
			</div>

			<form className="text-center">
				{preview && (
					<img src={preview} alt="picture" className="mb-4 border border-black p-2 rounded-lg w-[150px] h-[150px]" />
				)}

				<div
					className="mb-4 flex items-center border border-black p-2 rounded-lg w-1/4 text-sm hover:cursor-pointer"
					onClick={() => pictureRef.current.click()}
				>
					<input type="file" name="picture" id="picture" className="hidden" ref={pictureRef} onChange={handlePicture} />

					<img src="/src/assets/media/pictures/import.png" alt="" width={30} className="mr-2" />
					<label className="hover:cursor-pointer">Téléchargez l'image de votre produit</label>
				</div>

				<div className="2xl:flex items-center gap-2">
					<div className="flex flex-col">
						<label htmlFor="name" className="text-left pl-2">
							Nom
						</label>
						<input
							type="text"
							name="name"
							id="name"
							className="mb-4 border border-black p-2 rounded-lg"
							onChange={(e) => setName(e.target.value)}
						/>
					</div>

					<div className="flex flex-col">
						<label htmlFor="price" className="text-left pl-2">
							Prix
						</label>
						<input
							type="number"
							name="price"
							id="price"
							min={0}
							step={0.01}
							className="mb-4 border border-black p-2 rounded-lg"
							onChange={(e) => setPrice(e.target.value)}
						/>
					</div>

					<div className="flex flex-col">
						<label htmlFor="quantity" className="text-left pl-2">
							Quantity
						</label>
						<input
							type="number"
							name="quantity"
							id="quantity"
							min={0}
							step={0.01}
							className="mb-4 border border-black p-2 rounded-lg"
							onChange={(e) => setQuantity(e.target.value)}
						/>
					</div>

					<div className="flex flex-col">
						<label htmlFor="category" className="text-left pl-2">
							Catégorie
						</label>
						<select
							name="category"
							id="category"
							className="mb-4 border border-black p-2 rounded-lg"
							onChange={(e) => setSelectedCategory(e.target.value)}
						>
							<option value="">Sélectionnez une catégorie</option>

							{categories.map((category) => (
								<option key={category.id} value={category.id}>
									{category.name}
								</option>
							))}
						</select>
					</div>

					<div className="flex flex-col">
						<label htmlFor="weight" className="text-left pl-2">
							Poids (en g)
						</label>
						<input
							type="number"
							name="weight"
							id="weight"
							min={0}
							step="0.01"
							className="mb-4 border border-black p-2 rounded-lg"
							onChange={(e) => setWeight(e.target.value)}
						/>
					</div>

					<div className="flex flex-col">
						<label htmlFor="vat" className="text-left pl-2">
							Taux de TVA en %
						</label>
						<select
							name="vat"
							id="vat"
							className="mb-4 border border-black p-2 rounded-lg"
							onChange={(e) => setVat(e.target.value)}
						>
							<option value="">Sélectionnez une TVA</option>

							{vats.map((vat) => (
								<option key={vat.id} value={vat.id}>
									{vat.rate}
								</option>
							))}
						</select>
					</div>

					<div className="flex flex-col">
						<label htmlFor="max_quantity" className="text-left pl-2">
							Quantité maximale autorisée
						</label>
						<input
							type="number"
							name="max_quantity"
							id="max_quantity"
							min={0}
							className="mb-4 border border-black p-2 rounded-lg"
							onChange={(e) => setMaxQuantity(e.target.value)}
							placeholder="Illimité si non défini"
						/>
					</div>
				</div>

				<button
					type="submit"
					onClick={handleSubmit}
					className={`transition duration-300 text-white px-4 py-2 rounded-lg ${canSubmit
						? 'bg-primary hover:bg-primary/80 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary/70'
						: 'bg-gray-400 cursor-not-allowed'
						}`}
					disabled={!canSubmit}
				>
					Créer le produit
				</button>
			</form>
		</BOLayout>
	);
};

export default ProductCreation;
